/** The words of the activity's "Where it's used" panel (plan §3): its relations — the
 *  used-by's groups, one `type_key` each — what each row says about the risk of an edit or a
 *  delete, and when the activity is used nowhere. Pure, over used-by pages in the API's shape
 *  (`ActivityUsedByList`). */
import type {
  ActivityUsage,
  ActivityUsedByList,
  ActivityUsedByRef,
  ActivityUsedBySummary,
  ActivityUsedByType,
} from '@/api/activities';
import { consoleRouteFor } from '@/utils/gridLinks';

/** The panel's relations, in its order: the tours, then the rest of what refuses the delete
 *  (catalog itineraries, orphan day plans, travellers' own trips), then the stops and the
 *  parent activities. */
export const WHERE_USED_GROUPS = [
  'demo_group_tour',
  'demo_itinerary_group',
  'demo_itinerary',
  'demo_user_itinerary',
  'demo_vehicle_route_stop',
  'demo_activity',
] as const satisfies readonly ActivityUsedByType[];
export type WhereUsedGroupKey = (typeof WHERE_USED_GROUPS)[number];

/** The first rows of each group the panel shows; the rest are in the Usage tab. */
export const WHERE_USED_FIRST = 5;

export const WHERE_USED_LABEL: Readonly<Record<WhereUsedGroupKey, string>> = {
  demo_group_tour: 'Tours',
  demo_itinerary_group: 'Catalog itineraries',
  demo_itinerary: 'Orphan day plans',
  demo_user_itinerary: 'Unlisted trips',
  demo_vehicle_route_stop: 'Line-set stops',
  demo_activity: 'Nearby of',
};

/** What a relation's records are, in a sentence: its relation drawer's loading, failed and
 *  empty lines. */
export const WHERE_USED_NOUN: Readonly<Record<WhereUsedGroupKey, string>> = {
  demo_group_tour: 'tours',
  demo_itinerary_group: 'catalog itineraries',
  demo_itinerary: 'orphan day plans',
  demo_user_itinerary: 'unlisted trips',
  demo_vehicle_route_stop: 'line-set stops',
  demo_activity: 'activities with it nearby',
};

/** The relation drawer's title: what it lists, for the activity the app shows by `name`. */
export function whereUsedDrawerTitle(group: WhereUsedGroupKey, name: string): string {
  switch (group) {
    case 'demo_vehicle_route_stop':
      return `Line-set stops near ${name}`;
    case 'demo_activity':
      return `Activities with ${name} nearby`;
    default:
      return `${WHERE_USED_LABEL[group]} using ${name}`;
  }
}

export const WHERE_USED_EMPTY = 'Not used anywhere — safe to delete';

/** A row's mark when its record keeps the activity from being deleted. */
const BLOCKS_DELETE_MARK = 'blocks Delete';

/** What a count-only group (travellers' own trips, never listed) says in place of rows. */
export const WHERE_USED_UNLISTED =
  `Travellers’ own trips and trips of deleted tours · not listed · ${BLOCKS_DELETE_MARK}`;

// ---------------------------------------------------------------------------
// The activity's delete rule (plan §3 "Actions") — the one source of its words for every
// surface: the disabled Delete's reason, its confirmation, the refused delete's message and
// the rows' "blocks Delete" marks (help story 6.4 says the same, pinned by a test).
//
//  - Every relation whose rows say "blocks Delete" DISABLES Delete up front as soon as its
//    count is known and not zero: the catalog itineraries and the line-set stops from the
//    detail's cheap counts (`usage`), the tours once the used-by summary (or Where it's used)
//    has counted them, the orphan day plans and the unlisted trips once Where it's used has.
//    The reason names each with its count, in the panel's nouns.
//  - The server REFUSES it as well (`409 activity_in_use`, the backstop) while travellers'
//    trips use it — whatever the page had counted by then.
//
// The server's refusal is the day plans' PROTECT (`Trip_Flow` / `User_Trip_Flow`): tours,
// catalog itineraries, orphan day plans and travellers' trips refuse it there. A line-set stop
// would not — its link would go with the activity (CASCADE) — so the gate holds Delete back
// for it.
// ---------------------------------------------------------------------------

/** The relations whose rows say "blocks Delete" — each disables Delete up front once counted —
 *  in the panel's order, with one record's noun ("1 catalog itinerary"); their plural is the
 *  relation's `WHERE_USED_NOUN`. The nearby-of parents never block it. */
const DELETE_GATE = {
  demo_group_tour: 'tour',
  demo_itinerary_group: 'catalog itinerary',
  demo_itinerary: 'orphan day plan',
  demo_user_itinerary: 'unlisted trip',
  demo_vehicle_route_stop: 'line-set stop',
} as const satisfies Partial<Record<WhereUsedGroupKey, string>>;
export type DeleteGateKey = keyof typeof DELETE_GATE;
const DELETE_GATE_KEYS = Object.keys(DELETE_GATE) as DeleteGateKey[];

/** What disables Delete up front: "tours, catalog itineraries, orphan day plans, unlisted
 *  trips or line-set stops". */
export const DELETE_DISABLED_BY = (() => {
  const nouns = DELETE_GATE_KEYS.map((k) => WHERE_USED_NOUN[k]);
  return `${nouns.slice(0, -1).join(', ')} or ${nouns[nouns.length - 1]}`;
})();

/** What the server refuses the delete for besides (`409 activity_in_use`, the backstop). */
export const DELETE_REFUSED_BY = 'travellers’ trips';

/** What may still refuse a delete the gate let through: the travellers' trips — and the tours
 *  while nothing has counted them (not loaded yet, or the database cancelled its read):
 *  Delete is not disabled for them then. */
export function deleteRefusedBy(toursChecked: boolean): string {
  return toursChecked ? DELETE_REFUSED_BY : `${WHERE_USED_NOUN.demo_group_tour} or ${DELETE_REFUSED_BY}`;
}

/** One relation's count as the gate knows it; `capped`: the count stopped there ("500+"). */
export interface GateCount {
  count: number;
  capped?: boolean;
}

/** What the gate knows, by relation: absent or null while nothing has counted it. */
export type DeleteGateCounts = Partial<Record<DeleteGateKey, GateCount | null>>;

/** Why Delete is disabled, in the rule's words, counted — every blocking relation with a known
 *  count, in the panel's order: "Used by 7 tours · 1 catalog itinerary · 39 unlisted trips —
 *  remove it there first" ("500+ tours" past the summary's cap); null when none holds it. */
export function deleteDisabledReason(c: DeleteGateCounts): string | null {
  const parts = DELETE_GATE_KEYS.flatMap((key) => {
    const g = c[key];
    if (!g || !g.count) return [];
    const one = g.count === 1 && !g.capped;
    return [`${g.count.toLocaleString('en-US')}${g.capped ? '+' : ''} ${one ? DELETE_GATE[key] : WHERE_USED_NOUN[key]}`];
  });
  return parts.length ? `Used by ${parts.join(' · ')} — remove it there first` : null;
}

/** What the gate knows, from every source the page has: the detail's cheap counts (catalog
 *  itineraries, line-set stops), the used-by summary's tours (when it could count them) and
 *  Where it's used's groups (each relation's total, once its pages are in) — the larger where
 *  two sources count the same relation. */
export function deleteGateCounts(
  usage: Pick<ActivityUsage, 'catalog_itineraries' | 'stops'>,
  summary?: ActivityUsedBySummary | null,
  groups?: readonly WhereUsedGroup[] | null,
): DeleteGateCounts {
  const listed = new Map((groups ?? []).map((g) => [g.key, g]));
  const panel = (key: DeleteGateKey): GateCount | null => {
    const g = listed.get(key);
    return g ? { count: g.count, capped: g.capped } : null;
  };
  const most = (...known: (GateCount | null)[]): GateCount | null =>
    known.reduce<GateCount | null>((a, b) => (b && (!a || b.count > a.count) ? b : a), null);
  const summaryTours =
    summary?.available && summary.tours !== null ? { count: summary.tours, capped: summary.capped } : null;
  return {
    demo_group_tour: most(summaryTours, panel('demo_group_tour')),
    demo_itinerary_group: most({ count: usage.catalog_itineraries }, panel('demo_itinerary_group')),
    demo_itinerary: panel('demo_itinerary'),
    demo_user_itinerary: panel('demo_user_itinerary'),
    demo_vehicle_route_stop: most({ count: usage.stops }, panel('demo_vehicle_route_stop')),
  };
}

/** The groups whose rows say "blocks Delete": the gate's. */
const BLOCKS_DELETE: ReadonlySet<WhereUsedGroupKey> = new Set(DELETE_GATE_KEYS);

/** One relation of the panel. */
export interface WhereUsedGroup {
  key: WhereUsedGroupKey;
  /** Its total rows (the used-by's `available_types`), whatever the pages loaded. */
  count: number;
  /** The backend stopped counting at `count`: read "1,000+". */
  capped: boolean;
  /** Counted, never listed: it has no rows (travellers' own trips). */
  countOnly: boolean;
  /** The rows the pages loaded, in their order. */
  rows: ActivityUsedByRef[];
}

const isGroup = (key: string): key is WhereUsedGroupKey =>
  (WHERE_USED_GROUPS as readonly string[]).includes(key);

const isPage = (p: ActivityUsedByList | readonly ActivityUsedByList[]): p is ActivityUsedByList =>
  'available_types' in p;

/** The panel's relations from used-by pages — the first page, or one page per type (each
 *  page names every type's total in `available_types`) — in the panel's order. A row is
 *  kept once, however many pages carry it. */
export function whereUsedGroups(
  pages: ActivityUsedByList | readonly ActivityUsedByList[],
): WhereUsedGroup[] {
  const list = isPage(pages) ? [pages] : pages;
  const groups = new Map<WhereUsedGroupKey, WhereUsedGroup>(
    WHERE_USED_GROUPS.map((key) => [
      key,
      { key, count: 0, capped: false, countOnly: false, rows: [] },
    ]),
  );
  const seen = new Set<string>();
  for (const page of list) {
    for (const t of page.available_types) {
      const g = isGroup(t.key) ? groups.get(t.key) : undefined;
      if (!g) continue;
      g.count = Math.max(g.count, t.count);
      g.capped ||= !!t.capped;
      g.countOnly ||= !!t.count_only;
    }
    for (const row of page.results) {
      const g = isGroup(row.type_key) ? groups.get(row.type_key) : undefined;
      const id = `${row.type_key}:${row.id}`;
      if (!g || seen.has(id)) continue;
      seen.add(id);
      g.rows.push(row);
    }
  }
  return WHERE_USED_GROUPS.map((key) => {
    const g = groups.get(key)!;
    return { ...g, count: Math.max(g.count, g.rows.length) };
  });
}

/** The group's total in words: "9", "1,000+". */
export function groupCountText(g: Pick<WhereUsedGroup, 'count' | 'capped'>): string {
  return `${g.count.toLocaleString('en-US')}${g.capped ? '+' : ''}`;
}

/** "120 m", "1.2 km". */
export function formatDistance(metres: number): string {
  return metres < 1000 ? `${Math.round(metres)} m` : `${(metres / 1000).toFixed(1)} km`;
}

/** The row's facts after its name: the first day the activity is on (tours, catalog
 *  itineraries, orphan day plans), a stop's distance, and that it blocks Delete (every group
 *  but the parent activities). */
export function rowFacts(group: WhereUsedGroupKey, row: ActivityUsedByRef): string[] {
  const day = typeof row.day === 'number' ? `day ${row.day}` : null;
  const distance =
    group === 'demo_vehicle_route_stop' && typeof row.distance === 'number'
      ? formatDistance(row.distance)
      : null;
  const blocks = BLOCKS_DELETE.has(group) ? BLOCKS_DELETE_MARK : null;
  return [day, distance, blocks].filter((f): f is string => !!f);
}

/** The row's console page; null for a record without one (an orphan day plan). */
export function rowHref(row: Pick<ActivityUsedByRef, 'type_key' | 'id'>): string | null {
  return consoleRouteFor(row.type_key, row.id);
}

/** Nothing references the activity: no group counts or lists anything. */
export function isUnused(groups: readonly WhereUsedGroup[]): boolean {
  return groups.every((g) => g.count === 0 && g.rows.length === 0);
}

/** One relation's shape while the panel's rows load. */
export interface WhereUsedSkeletonGroup {
  key: WhereUsedGroupKey;
  /** The rows the loaded group will show: its count, at most `WHERE_USED_FIRST`. */
  rows: number;
  /** "N more in Usage ›" will follow the rows. */
  more: boolean;
}

/** The panel's shape while its rows load, from the detail's cheap counts (`usage`), so the
 *  rows land where the skeleton stood: the catalog itineraries, the line-set stops and the
 *  parent activities it counts, in the panel's order. Empty → the "Not used anywhere" line.
 *  The tours and the trips are not among those counts: they arrive with the rows. */
export function whereUsedSkeleton(
  usage: Pick<ActivityUsage, 'catalog_itineraries' | 'stops' | 'nearby_of'>,
): WhereUsedSkeletonGroup[] {
  const counted: Partial<Record<WhereUsedGroupKey, number>> = {
    demo_itinerary_group: usage.catalog_itineraries,
    demo_vehicle_route_stop: usage.stops,
    demo_activity: usage.nearby_of,
  };
  return WHERE_USED_GROUPS.flatMap((key) => {
    const n = counted[key] ?? 0;
    return n > 0 ? [{ key, rows: Math.min(n, WHERE_USED_FIRST), more: n > WHERE_USED_FIRST }] : [];
  });
}
